import React, { useState, useEffect } from "react";
import { Form, Input, Button, Upload, Avatar, Typography, Select, Switch, message, Card, Row, Col, Divider } from "antd";
import { UploadOutlined, UserOutlined, LinkedinOutlined, EnvironmentOutlined, EyeOutlined } from "@ant-design/icons";
import { saveProfile, saveProfileWithInviteToken, getProfileByInviteToken, getProfileByUserId } from '../../../api/api';
import { useUser } from '../../../context/auth.context';
import axios from "axios";
import { axiosInstant, endpoints } from "../../../api";
import moment from "moment";

const { Title } = Typography;
const { Option } = Select;

interface ProfileSetupModalProps {
  visible: boolean;
  onClose: () => void;
  initialEmail: string;
  inviteToken?: string;
  onProfileComplete?: (inviteToken?: string) => void;
}

const jobPreferences = [
  { label: "Remote", value: "remote" },
  { label: "Onsite", value: "onsite" },
  { label: "Hybrid", value: "hybrid" },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Returns a Promise<string> with the S3 URL
async function uploadFileToS3(file: File): Promise<string> {
  const keyName = `${moment().valueOf()}_${file.name}`;
  // 1. Get pre-signed URL
  const { data: uploadUrl } = await axiosInstant.post(endpoints.getPreSignedUrl, {
    keyName,
    contentType: file.type,
  });
  // 2. Upload file to S3
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type, "Content-Disposition": "inline" },
  });
  // 3. Return the S3 file URL (strip query params)
  return uploadUrl.split("?")[0];
}

const ProfileSetupModal: React.FC<ProfileSetupModalProps> = ({
  visible,
  initialEmail,
  inviteToken,
  onProfileComplete,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resumeFile, setResumeFile] = useState<string | undefined>();
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>();
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [resumeUploadFile, setResumeUploadFile] = useState<File | null>(null);
  const [imgError, setImgError] = useState(false);
  const { user } = useUser();

  // Watch the name field for live updates
  const firstName = Form.useWatch("firstName", form) || "";

  useEffect(() => {
    if (user && user._id) {
      getProfileByUserId(user._id).then(profileData => {
        setProfile(profileData);
        // Helper to handle both S3 and legacy URLs
        const getFileUrl = (url?: string) => {
          if (!url) return undefined;
          return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
        };
        setProfilePhoto(getFileUrl(profileData.profilePhoto));
        setResumeFile(getFileUrl(profileData.resume));
        form.setFieldsValue({
          email: initialEmail,
          ...profileData,
          isSearching: profileData.isSearching !== undefined ? profileData.isSearching : true,
        });
      });
    }
  }, [user._id]);

  useEffect(() => {
    console.log("Editing state changed:", editing);
  }, [editing]);

  console.log("ProfileSetupModal id prop:", user._id);
  console.log("ProfileSetupModal inviteToken prop:", inviteToken);

  const onFinish = async (values: any) => {
    // The resume is held in state rather than as a Form.Item, so antd's required
    // rules do not cover it; the profile is not complete without one.
    if (!resumeFile) {
      message.error("Please upload your resume before saving your profile.");
      return;
    }
    setLoading(true);
    const payload = {
      ...values,
      userId: user._id,
      _id: profile?._id,
      profilePhoto,
      resume: resumeFile,
    };
    let updatedProfile;
    try {
      if (user._id) {
        updatedProfile = await saveProfile(payload);
      } else if (inviteToken) {
        updatedProfile = await saveProfileWithInviteToken(payload, inviteToken);
      } else {
        message.error("No user ID or invite token provided.");
        setLoading(false);
        return;
      }

      setProfile(updatedProfile);
      const getFileUrl = (url?: string) => {
        if (!url) return undefined;
        return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
      };
      setProfilePhoto(getFileUrl(updatedProfile.profilePhoto));
      setResumeFile(getFileUrl(updatedProfile.resume));
      form.setFieldsValue(updatedProfile);

      setEditing(false);
      message.success("Profile updated successfully!");
      if (onProfileComplete) onProfileComplete(inviteToken);

    } catch (error) {
      console.error("Profile update failed:", error);
      message.error(`Profile update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    setLoading(false);
  };

  const handleResumeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const s3Url = await uploadFileToS3(file);
      setResumeFile(s3Url); // store S3 URL in state
    }
  };

  const handleProfilePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const s3Url = await uploadFileToS3(file);
      setProfilePhoto(s3Url); // store S3 URL in state
    }
  };

  console.log('DEBUG resumeFile:', resumeFile, 'editing:', editing);
  console.log('profilePhoto for <img>:', profilePhoto);

  return (
    <div style={{ maxWidth: 800, margin: "40px auto" }}>
      <Card
        style={{ borderRadius: 12, boxShadow: "0 2px 12px #f0f1f2" }}
        bodyStyle={{ padding: 32 }}
      >
        {/* Contextual message at the top */}
        {inviteToken && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ color: "#1890ff", fontWeight: 500, fontSize: '16px' }}>
              You've been invited to apply for a job. Please complete your profile to continue.
            </span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
          {/* Center: Title */}
          <div>
            <Title level={2} style={{ margin: 0, textAlign: "left", fontSize: '36px', fontWeight: 'bold', color: '#2f54eb' }}>
              {firstName ? `${firstName}'s Profile` : "Your Profile"}
            </Title>
          </div>
          {/* Right: Avatar and Upload */}
          <div style={{ textAlign: "center" }}>
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt="Profile"
                style={{
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #eee',
                  background: '#f0f0f0'
                }}
                onError={() => setImgError(true)}
              />
            ) : (
              <div
                style={{
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  background: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  color: '#aaa',
                  border: '2px solid #eee'
                }}
              >
                <span>?</span>
              </div>
            )}
            {editing && (
              <div style={{ marginTop: 12 }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  style={{ fontSize: '16px' }}
                />
              </div>
            )}
          </div>
        </div>
        
        <Form
          id="profileForm"
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ fontSize: '16px' }}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>First Name</span>} name="firstName" rules={[{ required: true }]}>
                <Input prefix={<UserOutlined />} placeholder="First Name" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Last Name</span>} name="lastName" rules={[{ required: true }]}>
                <Input prefix={<UserOutlined />} placeholder="Last Name" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Email</span>} name="email" rules={[{ required: true, type: "email" }]}>
                <Input placeholder="Email Address" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Phone Number</span>} name="phoneNumber" rules={[{ required: true}]}>
                <Input placeholder="Phone Number" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>LinkedIn</span>} name="linkedin" rules={[{ required: true }]}>
                <Input prefix={<LinkedinOutlined />} placeholder="LinkedIn URL" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
             
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Position Title</span>} name="positionTitle" rules={[{ required: true }]}>
                <Input placeholder="e.g. Software Engineer" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Years of Experience</span>} name="yearsOfExperience" rules={[{ required: true }]}>
                <Input type="number" min={0} placeholder="e.g. 5" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Job Preference</span>} name="jobPreference" rules={[{ required: true }]}>
                <Select placeholder="Select job preference" disabled={!editing} style={{ fontSize: '18px', height: '44px' }}>
                  {jobPreferences.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Location</span>} name="location" rules={[{ required: true }]}>
                <Input prefix={<EnvironmentOutlined />} placeholder="City, Country" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Relocation Preference</span>} name="relocation" rules={[{ required: true }]}>
                <Input placeholder="e.g. Open to relocate, Not open" disabled={!editing} style={{ fontSize: '18px', height: '44px' }} />
              </Form.Item>
              <Form.Item label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Resume</span>}>
                {resumeFile ? (
                  <Button
                    icon={<EyeOutlined />}
                    htmlType="button"
                    onClick={() => window.open(resumeFile, '_blank')}
                    style={{ fontSize: '18px', height: '44px' }}
                  >
                    Preview Resume
                  </Button>
                ) : (
                  <span style={{ color: '#888', fontSize: '18px' }}>No resume uploaded</span>
                )}
              </Form.Item>
              {editing && (
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleResumeChange}
                  style={{ fontSize: '16px' }}
                />
              )}
              {resumeFile && !resumeFile.toLowerCase().endsWith('.pdf') && (
                <div style={{ color: '#888', marginTop: 8 }}>
                  Note: Only PDF resumes are supported. Please upload your resume in PDF format.
                </div>
              )}
            </Col>
          </Row>
          <div>
            <Form.Item
              label={<span style={{ fontSize: '18px', fontWeight: 600 }}>Actively Searching?</span>}
              name="isSearching"
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<span style={{ fontSize: '16px' }}>Actively Searching</span>}
                unCheckedChildren={<span style={{ fontSize: '16px' }}>Not Searching</span>}
                disabled={!editing}
                style={{ minWidth: '160px', height: '28px' }}
              />
            </Form.Item>
          </div>
        </Form>
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Col>
            {!editing ? (
              <Button
                type="primary"
                htmlType="button"
                onClick={() => setEditing(true)}
                style={{ fontSize: '18px', height: '48px', padding: '0 28px' }}
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  htmlType="button"
                  loading={loading}
                  style={{ marginRight: 8, fontSize: '18px', height: '48px', padding: '0 28px' }}
                  onClick={() => form.submit()}
                >
                  Save Profile
                </Button>
                <Button
                  htmlType="button"
                  onClick={() => setEditing(false)}
                  style={{ fontSize: '18px', height: '48px', padding: '0 28px' }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ProfileSetupModal; 